<template>
  <b-row
    align-h="center"
    class="pt-6 ml-4 mr-4"
  >
    <b-col
      cols="12"
      md="9"
      lg="8"
      xl="8"
      xxl="6"
    >
      <b-row align-h="center">
        <b-col
          cols="12"
          md="9"
          class="mb-5"
        >
          <h1 class="mb-3 text-center heading text-primary">
            Medical Information
          </h1>
          <p class="sub-text text-center">
            If you would like to update your medical information please message
            your doctor <b-link to="/messages/doctor">
              here
            </b-link>
          </p>
          <div class="d-block d-md-none pb-3"></div>
        </b-col>
        <b-col
          cols="12"
          md="9"
          class="mb-5"
        >
          <p class="sub-text">
            Obtain your medical records by clicking on the DOWNLOAD button
          </p>
          <b-button
            pill
            class="slight-rounded"
            variant="info"
            size="lg"
            @click="downloadMedicalInformation"
          >
            DOWNLOAD
          </b-button>
        </b-col>
        <b-col
          v-for="(healthQuestion, index) in healthQuestions"
          :key="index"
          class="mb-3"
          cols="12"
          md="9"
        >
          <div
            role="group"
            class="mb-3"
          >
            <label
              class="sub-text mb-1 text-uppercase"
              :for="`healthQuestion${index}`"
            >{{ index + 1 }}. {{ healthQuestion.question }}</label>
            <p
              v-if="healthQuestion.question != 'Stated allergies'"
              :id="`healthQuestion${index}`"
              class="mb-2 tw-whitespace-pre-wrap"
              :style="`color:${healthQuestion.highlightcolor}`"
            >
              {{ healthQuestion.answer && healthQuestion.answer.replace(/\t/g, '\n').replace(/(\r\n|\n|\r)/gm, '\n') }}
            </p>
            <p
              v-else
              class="mb-2 tw-whitespace-pre-wrap"
              :style="`color:${healthQuestion.highlightcolor}`"
            >
              {{ healthQuestion.answer && healthQuestion.answer.replace(/\t/g, '\n').replace(/(\r\n|\n|\r)/gm, '\n') }}
            </p>
            <hr class="my-2">
          </div>
        </b-col>
      </b-row>

      <!-- edit medical information -->
      <template v-if="editInformation">
        <h2
          class="mb-6 text-primary"
          style="font-size: 26px"
        >
          Personal Information
        </h2>

        <b-alert
          :show="alert.show"
          :variant="alert.type"
        >
          {{
            alert.message
          }}
        </b-alert>
        <b-row>
          <b-col
            cols="12"
            md="6"
          >
            <div
              role="group"
              class="mb-3"
            >
              <label
                for="firstName"
                class="d-letter-spacing"
              >First Name:</label>
              <b-form-input
                id="firstName"
                v-model="user.firstname"
                class="fs-mask"
                type="text"
              />
            </div>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <div
              role="group"
              class="mb-3"
            >
              <label
                for="lastName"
                class="d-letter-spacing"
              >Last Name:</label>
              <b-form-input
                id="lastName"
                v-model="user.lastname"
                class="fs-mask"
                type="text"
              />
            </div>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <div
              role="group"
              class="mb-3"
            >
              <label
                for="birthday"
                class="d-letter-spacing"
              >Date of Birth:</label>
              <flat-pickr
                id="birthday"
                v-model="user.birthday"
                autocomplete="off"
                class="form-control pl-0 fs-mask"
                placeholder="MM/DD/YYYY"
                :config="config"
              />
            </div>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <div
              role="group"
              class="mb-3"
            >
              <label
                for="gender"
                class="d-letter-spacing"
              >Gender:</label>
              <v-select
                v-model="gender"
                append-to-body
                :calculate-position="withPopper"
                label="text"
                :options="genderOption"
                :reduce="(genderOption) => genderOption.value"
                disabled
              >
                <template #open-indicator="{ attributes }">
                  <b-icon
                    v-bind="attributes"
                    icon="chevron-down"
                  />
                </template>
              </v-select>
              <!-- <b-form-input
                id="gender"
                type="text"
              ></b-form-input> -->
            </div>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <div
              role="group"
              class="mb-3"
            >
              <label
                for="emailAddress"
                class="d-letter-spacing"
              >Email Address:</label>
              <b-form-input
                id="emailAddress"
                v-model="user.email"
                class="fs-mask"
                type="text"
                disabled
              />
            </div>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <div
              role="group"
              class="mb-4"
            >
              <label
                for="phone"
                class="d-letter-spacing"
              >Phone Number:</label>
              <b-form-input
                id="phone"
                v-model="user.phoneno"
                class="fs-mask"
                :formatter="formatPhoneNumber"
              />
            </div>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <div
              role="group"
              class="mb-3"
            >
              <label
                for="streetAddress"
                class="d-letter-spacing"
              >Street Address:</label>
              <b-form-input
                id="streetAddress"
                v-model="shippingDetails.address1"
                type="text"
              />
            </div>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <div
              role="group"
              class="mb-3"
            >
              <label
                for="apartment"
                class="d-letter-spacing"
              >Apartment/Suite:</label>
              <b-form-input
                id="apartment"
                v-model="shippingDetails.address2"
                type="text"
              />
            </div>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <div
              role="group"
              class="mb-3"
            >
              <label
                for="city"
                class="d-letter-spacing"
              >City:</label>
              <b-form-input
                id="city"
                v-model="shippingDetails.city"
                type="text"
              />
            </div>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <div
              role="group"
              class="mb-3"
            >
              <label
                for="state"
                class="d-letter-spacing"
              >State:</label>
              <v-select
                id="state"
                v-model="selectedState"
                type="text"
                :options="stateOptions"
              />
            </div>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <div
              role="group"
              class="mb-3"
            >
              <label
                for="country"
                class="d-letter-spacing"
              >Country:</label>
              <b-form-input
                id="country"
                v-model="shippingDetails.country"
                type="text"
                readonly
              />
            </div>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <div
              role="group"
              class="mb-3"
            >
              <label
                for="zip"
                class="d-letter-spacing"
              >Zip Code:</label>
              <b-form-input
                id="zip"
                v-model="shippingDetails.zipcode"
                type="text"
              />
            </div>
          </b-col>
        </b-row>
        <b-row class="mt-3 mb-3">
          <b-col
            cols="12"
            md="6"
          />
          <b-col
            cols="6"
            md="3"
          >
            <b-button
              pill
              block
              variant="outline-info"
              @click="editInformation = false"
            >
              Cancel
            </b-button>
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <b-button
              pill
              block
              variant="info"
              @click="saveUpdate"
            >
              Update
            </b-button>
          </b-col>
        </b-row>
      </template>

      <!-- intake form -->
      <template v-if="intakeForm">
        <h2
          class="mb-6 text-primary"
          style="font-size: 26px"
        >
          Medical History
        </h2>
        <b-row>
          <b-col
            cols="12"
            class="mb-3"
          >
            <b-row
              align-v="center"
              class="mb-4"
            >
              <b-col
                cols="12"
                class="mb-3"
              >
                <h3 class="bold d-letter-spacing">
                  Health Questions
                </h3>
              </b-col>

              <b-col cols="12">
                <b-row>
                  <b-col
                    v-for="(healthQuestion, index) in healthQuestions"
                    :key="index"
                    cols="12"
                    md="6"
                  >
                    <div
                      role="group"
                      class="mb-3"
                    >
                      <label
                        class="d-letter-spacing"
                        :for="`healthQuestion${index}`"
                      >{{ index + 1 }}. {{ healthQuestion.question }}</label>
                      <span
                        v-if="healthQuestion.question != 'Stated allergies'"
                        :id="`healthQuestion${index}`"
                        class="pl-0 tw-block tw-whitespace-pre-wrap"
                        :style="`color:${healthQuestion.highlightcolor}`"
                      >{{ healthQuestion.answer && healthQuestion.answer.replace(/\t/g, '\n') }} haha</span>
                      <b-form-textarea
                        v-else
                        disabled
                        max-rows="15"
                        class="pl-0"
                        type="text"
                        style="overflow: hidden"
                        :value="
                          healthQuestion.answer.replace(
                            /(\r\n|\n|\r)/gm,
                            '\r\n'
                          )
                        "
                        :style="`color:${healthQuestion.highlightcolor}`"
                      />
                    </div>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
        <b-row class="mb-5">
          <b-col
            cols="12"
            md="6"
          />
          <b-col
            cols="6"
            md="3"
          />
          <b-col
            cols="6"
            md="3"
          >
            <b-button
              pill
              block
              variant="info"
              @click="saveUpdate"
            >
              Back
            </b-button>
          </b-col>
        </b-row>
      </template>
    </b-col>
    <Loading
      :active.sync="loading"
      :can-cancel="false"
      color="#1a6a72"
    />
  </b-row>
</template>

<script>
import { mapGetters } from 'vuex';
import _ from 'lodash';

import { LookUpService } from '@/services/lookup.service';
import { PatientService } from '@/services/patient.service';

import popper from '@/assets/js/popper.js';
import mixins from '@/components/form/mixins';
import { downloadUrl } from '@/utils/downloadUrl';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';

export default {
  name: 'MedicalInformation',
  components: {
    Loading,
  },
  mixins: [mixins, popper],
  data() {
    return {
      alert: {
        show: false,
        type: 'warning',
        message: '',
      },
      editInformation: false,
      intakeForm: false,
      config: {
        altInput: true,
        altFormat: 'm/d/Y',
        allowInput: true,
      },
      states: [],
      stateOptions: [],
      selectedState: '',
      gender: 'Female',
      genderOption: [
        { value: 'male', text: 'Male' },
        { value: 'female', text: 'Female' },
      ],
      loading: false,
    };
  },
  computed: {
    ...mapGetters({
      user: 'user/getUser',
      shippingDetails: 'user/getDefaultShipping',
      healthQuestions: 'user/getHealthQuestions',
    }),
  },
  watch: {
    selectedState() {
      this.shippingDetails.state = this.states.filter(
        state => state.name == this.selectedState
      )[0].id;
    },
  },
  async mounted() {
    await this.$store.dispatch('user/getUser');
    await this.$store.dispatch('user/getDefaultShipping');
    await this.$store.dispatch('user/getHealthQuestions');
    await this.getStates();

    // * Currently hardcoding the country data
    this.shippingDetails.country = 'US';
    this.user.birthday = this.formatDate(this.user.birthday);
    this.user.phoneno = this.formatPhoneNumber(this.user.phoneno);
  },
  methods: {
    showAlert(message, ) {
      this.alert = {
        show: true,
        type: 'warning',
        message: message,
      };
    },
    formatDate(date) {
      const d = new Date(date)
      let month = '' + (d.getMonth() + 1)
      let day = '' + d.getDate()
      const year = d.getFullYear();

      if (month.length < 2) month = '0' + month;
      if (day.length < 2) day = '0' + day;

      return [year, month, day].join('/');
    },
    async saveUpdate() {
      try {
        const filterUser = 'firstname, lastname, birthday, phoneno';
        const userDetails = _.pick(this.user, filterUser.split(', '));
        userDetails.birthday = this.formatDate(userDetails.birthday);
        this.$store
          .dispatch('user/updateUser', userDetails)
          .then(res => {
            if (res && res.message) {
              this.showAlert(res.message);
              return;
            } else {
              const shippingId = this.shippingDetails.id;
              const filterShipping =
                'address1, address2, zipcode, country, city, state';
              const shippingDetails = _.pick(
                this.shippingDetails,
                filterShipping.split(', ')
              );

              (shippingDetails.firstname = userDetails.firstname),
              (shippingDetails.lastname = userDetails.lastname);

              this.$store
                .dispatch('user/updateShippingDetails', {
                  id: shippingId,
                  data: shippingDetails,
                })
                .then(res => {
                  if (res && res.message) {
                    this.showAlert(res.message);
                    return;
                  }
                  this.editInformation = false;
                  this.intakeForm = false;
                });
            }
          })
          .catch(err => {
            console.log(err);
          });
      } catch (error) {
        console.log(error.message);
        this.showAlert(error.message);
      }
    },
    async getStates() {
      const { data } = await LookUpService.getLookUp('state');
      if (data) {
        this.states = data;
        for (const index in data) {
          this.stateOptions[index] = data[index].name;
        }
      }

      // * Gets the user's pre-selected state.
      if (this.shippingDetails.state) {
        this.selectedState = this.states.filter(
          state => state.id == this.shippingDetails.state
        )[0].name;
      }
    },
    async downloadMedicalInformation() {
      this.loading = true
      try {
        const { data } = await PatientService.getMedicalRecords(this.user.id);
        const name = '';
        if (this.user.firstname) {
          name.concat(this.user.firstname);
        }
        if (this.user.lastname) {
          name.concat(this.user.lastname);
        }
        const title = `${name}_WinonaMedicalRecords`

        downloadUrl(data, title)
      } catch (err) {
        console.error(err);
        this.$emit('show-toast', 'Failed to download medical records.');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
label {
  font-size: 11px;
  color: #828282;
}

.form-control {
  padding-left: 0;
  color: #19191c;
  border-color: #828282;
}

@media (min-width: 760px) {
  .offset {
    margin-left: 200px;
  }
}
</style>
